import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import axios from 'axios';
import '../styles/AccountPage.css';
import '../styles/AuthPage.css';

const AccountPage = () => {
  const { user, loading: authLoading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [editingPassword, setEditingPassword] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileData, setProfileData] = useState({
    email: ''
  });
  const [verificationData, setVerificationData] = useState({
    organizerName: '',
    description: '',
    website: '',
    instagram: '',
    tiktok: ''
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }
    fetchAccountData();
  }, [user, authLoading, navigate]);

  const fetchAccountData = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountData(data);
      setProfileData({
        email: data.email
      });
      setVerificationData({
        organizerName: data.organizerName || data.profileName || '',
        description: data.description || '',
        website: data.website || '',
        instagram: data.instagram || '',
        tiktok: data.tiktok || ''
      });
    } catch (error) {
      console.error('Error fetching account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handleVerificationChange = (e) => {
    setVerificationData({
      ...verificationData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        '/api/auth/profile',
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAccountData(data);
      setEditingProfile(false);
      showToast('Profil erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast(error.response?.data?.message || 'Fehler beim Aktualisieren des Profils', 'error');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('Die neuen Passwörter stimmen nicht überein', 'error');
      return;
    }

    // Password policy validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
    if (!passwordRegex.test(passwordData.newPassword)) {
      showToast('Passwort muss mindestens 8 Zeichen lang sein und Grossbuchstaben, Kleinbuchstaben, Zahlen und Sonderzeichen enthalten', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        '/api/auth/password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setEditingPassword(false);
      showToast('Passwort erfolgreich geändert', 'success');
    } catch (error) {
      console.error('Error changing password:', error);
      showToast(error.response?.data?.message || 'Fehler beim Ändern des Passworts', 'error');
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();

    // Validate URLs
    const urlPattern = /^https?:\/\/.+/;
    if (verificationData.website && !urlPattern.test(verificationData.website)) {
      showToast('Webseite muss eine gültige URL sein (mit http:// oder https://)', 'error');
      return;
    }
    if (verificationData.instagram && !verificationData.instagram.includes('instagram.com')) {
      showToast('Bitte geben Sie einen gültigen Instagram-Link ein', 'error');
      return;
    }
    if (verificationData.tiktok && !verificationData.tiktok.includes('tiktok.com')) {
      showToast('Bitte geben Sie einen gültigen TikTok-Link ein', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        '/api/auth/become-verified',
        verificationData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAccountData(data);
      setShowVerificationForm(false);
      showToast('Ihre Verifizierungsanfrage wurde eingereicht und wird von einem Administrator geprüft.', 'success');
    } catch (error) {
      console.error('Error becoming verified:', error);
      showToast(error.response?.data?.message || 'Fehler bei der Verifizierung', 'error');
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/auth/account', {
        headers: { Authorization: `Bearer ${token}` },
        data: { password: deletePassword }
      });

      // Logout and redirect to home
      logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast(error.response?.data?.message || 'Fehler beim Löschen des Accounts', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (authLoading || loading) return <div className="loading">Account wird geladen...</div>;
  if (!accountData) return <div className="error">Account nicht gefunden</div>;

  return (
    <div className="account-page">
      <div className="account-header">
        <h1>Mein Account</h1>
      </div>

      <div className="account-content">
        <div className="account-section">
          <h2>Account-Informationen</h2>

{!editingProfile && !editingPassword && !showVerificationForm ? (
            <>
              <div className="account-info">
                <div className="info-item">
                  <strong>E-Mail:</strong> {accountData.email}
                </div>
                <div className="info-item">
                  <strong>Rolle:</strong> {accountData.role === 'admin' ? 'Administrator' : (accountData.isVerified ? 'Verifizierter Veranstalter' : 'Veranstalter')}
                  {accountData.isVerified && (
                    <span className="verified-badge" title="Verifizierter Veranstalter">✓</span>
                  )}
                </div>
              </div>
              <div className="form-actions">
<button onClick={() => {
                  setEditingPassword(true);
                }} className="btn btn-secondary">
                  Passwort ändern
                </button>
              </div>

              {!accountData.isVerified && accountData.verificationStatus === 'pending' && (
                <div className="verification-section pending">
                  <h3>Verifizierung ausstehend</h3>
                  <p>Ihre Verifizierungsanfrage wird derzeit von einem Administrator geprüft. Sie werden benachrichtigt, sobald Ihre Anfrage bearbeitet wurde.</p>
                  <div className="pending-info">
                    {accountData.organizerName && (
                      <div><strong>Veranstaltername:</strong> {accountData.organizerName}</div>
                    )}
                    {accountData.description && (
                      <div><strong>Beschreibung:</strong> {accountData.description}</div>
                    )}
                    {accountData.website && (
                      <div><strong>Webseite:</strong> <a href={accountData.website} target="_blank" rel="noopener noreferrer">{accountData.website}</a></div>
                    )}
                    {accountData.instagram && (
                      <div><strong>Instagram:</strong> <a href={accountData.instagram} target="_blank" rel="noopener noreferrer">{accountData.instagram}</a></div>
                    )}
                    {accountData.tiktok && (
                      <div><strong>TikTok:</strong> <a href={accountData.tiktok} target="_blank" rel="noopener noreferrer">{accountData.tiktok}</a></div>
                    )}
                  </div>
                </div>
              )}

              {!accountData.isVerified && accountData.verificationStatus === 'rejected' && (
                <div className="verification-section rejected">
                  <h3>Verifizierung abgelehnt</h3>
                  <p>Ihre Verifizierungsanfrage wurde leider abgelehnt. Sie können eine neue Anfrage mit aktualisierten Informationen einreichen.</p>
                  <button
                    onClick={() => {
                      setShowVerificationForm(true);
                    }}
                    className="btn btn-primary"
                  >
                    Erneut beantragen
                  </button>
                </div>
              )}

              {!accountData.isVerified && (!accountData.verificationStatus || accountData.verificationStatus === 'none') && (
                <div className="verification-section">
                  <h3>Verifizierter Veranstalter werden</h3>
                  <p>Als verifizierter Veranstalter erhalten Sie ein Verifizierungsabzeichen und können zusätzliche Informationen wie Beschreibung, Webseite und Social-Media-Links hinzufügen.</p>
                  <button
                    onClick={() => {
                      setShowVerificationForm(true);
                    }}
                    className="btn btn-primary"
                  >
                    Jetzt verifizieren
                  </button>
                </div>
              )}
            </>
          ) : editingProfile ? (
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-group">
                <label>E-Mail *</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Änderungen speichern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingProfile(false);
                    setProfileData({
                      email: accountData.email
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          ) : showVerificationForm ? (
            <form onSubmit={handleVerificationSubmit} className="verification-form">
              <h3>Verifizierter Veranstalter werden</h3>

              <div className="form-group">
                <label>Veranstaltername</label>
                <input
                  type="text"
                  name="organizerName"
                  value={verificationData.organizerName}
                  onChange={handleVerificationChange}
                  placeholder="Name der angezeigt werden soll"
                />
              </div>

              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  name="description"
                  value={verificationData.description}
                  onChange={handleVerificationChange}
                  rows="4"
                  placeholder="Erzählen Sie etwas über sich oder Ihre Organisation..."
                />
              </div>

              <div className="form-group">
                <label>Webseite</label>
                <input
                  type="text"
                  name="website"
                  value={verificationData.website}
                  onChange={handleVerificationChange}
                  placeholder="https://www.beispiel.ch"
                />
              </div>

              <div className="form-group">
                <label>Instagram</label>
                <input
                  type="text"
                  name="instagram"
                  value={verificationData.instagram}
                  onChange={handleVerificationChange}
                  placeholder="https://www.instagram.com/benutzername"
                />
              </div>

              <div className="form-group">
                <label>TikTok</label>
                <input
                  type="text"
                  name="tiktok"
                  value={verificationData.tiktok}
                  onChange={handleVerificationChange}
                  placeholder="https://www.tiktok.com/@benutzername"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Verifizierung abschliessen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowVerificationForm(false);
                    setVerificationData({
                      organizerName: accountData.organizerName || accountData.profileName || '',
                      description: accountData.description || '',
                      website: accountData.website || '',
                      instagram: accountData.instagram || '',
                      tiktok: accountData.tiktok || ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="form-group">
                <label>Aktuelles Passwort</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Neues Passwort
                  <span className="info-tooltip">
                    <span className="info-icon">i</span>
                    <span className="info-text">Mindestens 8 Zeichen, Gross- und Kleinbuchstaben, Zahlen und Sonderzeichen</span>
                  </span>
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                />
              </div>

              <div className="form-group">
                <label>Neues Passwort bestätigen</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Passwort speichern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingPassword(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Delete Account Section - Not available for admins */}
        {accountData.role !== 'admin' && (
        <div className="account-section delete-section">
          <h2>Account löschen</h2>

          {!showDeleteForm ? (
            <div className="delete-info">
              <p className="delete-warning">
                <strong>Achtung:</strong> Das Löschen Ihres Accounts ist unwiderruflich.
                Alle Ihre Events werden ebenfalls gelöscht.
              </p>
              <button
                onClick={() => {
                  setShowDeleteForm(true);
                }}
                className="btn btn-danger"
              >
                Account löschen
              </button>
            </div>
          ) : (
            <form onSubmit={handleDeleteAccount} className="delete-form">
              <p className="delete-warning">
                <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.
                Alle Ihre Events werden ebenfalls permanent gelöscht.
              </p>

              <div className="form-group">
                <label>Passwort zur Bestätigung</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  required
                  placeholder="Ihr aktuelles Passwort eingeben"
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Wird gelöscht...' : 'Account endgültig löschen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteForm(false);
                    setDeletePassword('');
                }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
